import { action, payload } from "ts-action";
//#region src/store/async-action-utils.ts
/**
* Async Action Utilities
*
* Provides a factory for creating async action groups (action/success/fail)
* with associated loading state configuration.
*
* This eliminates boilerplate by:
* 1. Defining all 3 actions in one place
* 2. Attaching loading/toast configuration directly to the action group
* 3. Enabling generic effects that work with any action group
*/
/**
* Creates an async action group with action/success/fail creators and config.
*
* @param baseType - Base action type (e.g., "LOAD_PROFILE")
* @param options - Loading state and toast configuration
* @returns AsyncActionGroup with all creators and config
*
* @example
* ```typescript
* // Without payload on action
* export const loadProfile = createAsyncAction<ProfileData>("LOAD_PROFILE", {
*   loadingTxt: msg("Loading profile...", { id: "..." }),
*   modal: true,
* });
*
* // With typed payloads
* export const updateProfile = createAsyncActionGroup<
*   UpdateData,     // Action payload
*   ProfileData,    // Success payload
* >("UPDATE_PROFILE", {
*   loadingTxt: "Updating...",
*   successTxt: "Profile updated!",
*   modal: true,
* });
*
* // Usage in effect with ofType():
* actions$.pipe(
*   ofType(loadProfile.action),
*   mergeMap(() => profileService.load().pipe(
*     map((data) => loadProfile.success(data)),
*     catchError((err) => of(loadProfile.fail(err)))
*   ))
* );
*
* // Usage in reducer with on():
* reducer(
*   initialState,
*   on(loadProfile.success, (state, { payload }) => ({
*     ...state,
*     profile: payload
*   }))
* );
* ```
*/
function createAsyncActionGroup(baseType, options) {
	const types = {
		action: baseType,
		success: `${baseType}_SUCCESS`,
		fail: `${baseType}_FAIL`
	};
	const config = {
		actionKey: options.actionKey || baseType.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()),
		loadingTxt: options.loadingTxt,
		successTxt: options.successTxt,
		modal: options.modal,
		smallBackground: options.smallBackground,
		successDuration: options.successDuration,
		loaderStyle: options.loaderStyle
	};
	return {
		action: action(types.action, payload()),
		success: action(types.success, payload()),
		fail: action(types.fail, payload()),
		config,
		baseType,
		types
	};
}
/**
* Creates an async action group without payload on the initiating action.
* This is a convenience wrapper for the common case.
*
* @example
* ```typescript
* export const loadProfile = createAsyncAction<ProfileData>("LOAD_PROFILE", {
*   loadingTxt: "Loading...",
*   modal: true,
* });
*
* // loadProfile.action() - no payload
* // loadProfile.success(data) - ProfileData payload
* // loadProfile.fail(error) - Error payload
* ```
*/
function createAsyncAction(baseType, options) {
	return createAsyncActionGroup(baseType, options);
}
/**
* Type guard to check if an action belongs to an async action group.
*/
function isActionFromGroup(actionObj, group) {
	return actionObj.type === group.types.action || actionObj.type === group.types.success || actionObj.type === group.types.fail;
}
/**
* Gets the config for an action from a list of groups.
* Returns undefined if no matching group is found.
*/
function getConfigForAction(actionObj, groups) {
	return groups.find((g) => isActionFromGroup(actionObj, g))?.config;
}
//#endregion
export { createAsyncAction, createAsyncActionGroup, getConfigForAction, isActionFromGroup };
